import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Box, Grid, lighten, makeStyles, Theme, Typography, useTheme } from '@material-ui/core';
import { Menu } from '@material-ui/icons';
import { LoginLogoImg, Page } from '@roc/feature-app-core';
import { IconButton, Paper, PaperBorderType, PreviewBackdrop } from '@roc/ui';
import clsx from 'clsx';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import { HtmlDocumentMenu } from '../../components/htmlDocumentMenu';
import { useDocumentLibraryRoutes } from '../../hooks/useDocumentLibraryRoutes';

const useStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    position: 'relative',
    height: '100%',
    padding: '16px',
    background: '#FFF',
    fontSize: 16,

    '& p': {
      marginBottom: 5,
      marginTop: 10,
    },
    '& ul,ol': {
      marginLeft: theme.spacing(3),
      marginBottom: theme.spacing(0.5),
    },
    '& h6': {
      fontSize: 16,
      padding: 4,
      fontWeight: 'bold'
    },
    '& h4': {
      marginTop: '30px',
      fontWeight: 'bold',
      marginBottom: 5,
      background: lighten(theme.palette.primary.main, 0.8),
    },
    '& h3': {
      marginTop: '30px',
      fontWeight: 'bold',
      marginBottom: 5,
    },
  },
  documentCard: {
    borderRadius: 0,
    marginBottom: theme.spacing(1),
    borderColor: '#aaa',
  },
  formOfAppraisaldocumentCard: {
    borderRadius: 0,
    marginBottom: theme.spacing(1),
    borderColor: '#aaa',
    '& div': {
      padding: 0,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      textAlign: 'center',
    },
    '& table th': {
      background: lighten(theme.palette.primary.main, 0.8),
    },
    '& table td': {
      borderBottom: '0.25px solid grey',
      textAlign: 'left',
    },
  },
  appraisalCompaniesdocumentCard: {
    borderRadius: 0,
    marginBottom: theme.spacing(1),
    borderColor: '#aaa',

    '& table th': {
      background: lighten(theme.palette.primary.main, 0.5),
    },
    '& table th:nth-child(1)': {
      background: 'white',
    },
    '& table td': {
      textAlign: 'center',
    },
    '& table td:nth-child(1)': {
      background: lighten(theme.palette.primary.main, 0.8),
    },
  },
  generalRequirementsdocumentCard: {
    justifyContent: 'left',
    marginTop: '5%',
    textAlign: 'left',
    width: 'fit-content',
    marginBottom: theme.spacing(1),
    borderRadius: 0,
    borderColor: '#aaa',
    '& div': {
      padding: 0,
    },
    '& table td': {
      maxWidth: '600px',
      '& p': {
        marginRight: '5%'
      }
    },
    border: 'none'
  },
  documentCardTitle: {
    justifyContent: 'center',
    textAlign: 'center',
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    padding: theme.spacing(0.5),
  },
  leftCardTitle: {
    justifyContent: 'left',
    textAlign: 'left',
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    width: 'fit-content',
    marginBottom: theme.spacing(1)
  }

}));

const usePrintModeStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    fontSize: 10,
    '& ul,ol': {
      marginLeft: theme.spacing(2),
    },
    '& h6': {
      fontSize: 12,
    },
  },
}));

const DocumentCard = ({
  title = undefined,
  data,
  paperBorder = PaperBorderType.SOLID_BORDER,
  paperClassName = '',
  paperTitleClassName = '',
  paperBodyClassName = '',
  paperStyle = {},
}) => {
  const classes = useStyles();
  return (
    <Paper
      title={title}
      titleClass={clsx(classes.documentCardTitle, paperTitleClassName)}
      titleVariant={'h6'}
      borderType={paperBorder}
      className={clsx(classes.documentCard, paperClassName)}
      style={paperStyle}>
      <Box p={0.5} className={paperBodyClassName}>
        {documentToReactComponents(data)}
      </Box>
    </Paper>
  )
}

export const TermLoansAppraisalGuidelinesHtml = ({
  contentId,
  data,
  preview = false,
  printMode = false,
  setContainerRef,
}: {
  contentId: string,
  data: any,
  preview?: boolean,
  printMode?: boolean,
  setContainerRef: any,
}) => {
  const theme = useTheme();
  const { documentLibraryRoutesConfig } = useDocumentLibraryRoutes();
  const classes = useStyles();
  const printModeClasses = usePrintModeStyles();
  const containerRef = useRef(null);

  const [printModeContainerStyle, setPrintModeContainerStyle] = useState<any>();
  const [containerStyle, setContainerStyle] = useState<any>();

  useEffect(() => {
    const doc = new jsPDF("p", "mm", "a4");
    let width: any = doc.internal.pageSize.getWidth();
    let height: any = doc.internal.pageSize.getHeight();
    width = `${Math.floor(width)}mm`;
    height = `${Math.floor(height)}mm`;
    setPrintModeContainerStyle({
      margin: 'auto',
      minWidth: width,
      maxWidth: width,
      height: height
    });
    setContainerStyle({
      margin: 'auto',
      maxWidth: theme.breakpoints.values.lg,
    });
  }, []);

  useEffect(() => {
    setContainerRef(containerRef);
  }, [containerRef])

  if (!data) {
    return null;
  }

  return (
    <div ref={containerRef} style={printMode ? printModeContainerStyle : containerStyle}>
      <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box padding={'16px'} className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>

            <Typography style={{ marginTop: '10px' }} variant={'h5'}>
              <strong>{data.documentTitle}</strong>
            </Typography>
          </Box>

          <Box mb={2} paddingLeft={'5%'} paddingRight={'5%'} paddingBottom="5%" overflow={'auto'} >
            <Grid container >
              <Grid item xs={12}>

                <DocumentCard
                  title={data.formOfAppraisalByPropertyTypeTitle}
                  paperTitleClassName={classes.leftCardTitle}
                  paperClassName={classes.formOfAppraisaldocumentCard}
                  data={data.formOfAppraisalByPropertyTypeCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />

                <DocumentCard
                  title={data.generalAppraisalRequirementsTitle}
                  paperTitleClassName={classes.leftCardTitle}
                  paperClassName={classes.generalRequirementsdocumentCard}
                  data={data.generalAppraisalRequirementsCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />


              </Grid>
            </Grid>
          </Box>
        </Box>
      </Page>

      <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box padding={'16px'} className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>

            <Typography style={{ marginTop: '10px' }} variant={'h5'}>
              <strong>{data.documentTitle}</strong>
            </Typography>
          </Box>

          <Box mb={2} paddingLeft={'5%'} paddingRight={'5%'} paddingBottom="5%" overflow={'auto'} >
            <Grid container >
              <Grid item xs={12}>


                <DocumentCard
                  title={data.appraisalManagementCompaniesTitle}
                  paperTitleClassName={classes.leftCardTitle}
                  data={data.appraisalManagementCompaniesCard}
                  paperClassName={classes.appraisalCompaniesdocumentCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />


              </Grid>
            </Grid>
          </Box>
        </Box>
      </Page>

      <Page routeDefinition={documentLibraryRoutesConfig.documentLibraryDocument(contentId)}>
        <Box padding={'16px'} className={clsx(classes.documentContainer, printMode ? printModeClasses.documentContainer : undefined)}>
          {preview && <PreviewBackdrop />}
          <LoginLogoImg imgStyle={{ maxWidth: 200 }} />
          <Box my={2} textAlign='center'>

            <Typography style={{ marginTop: '10px' }} variant={'h5'}>
              <strong>{data.documentTitle}</strong>
            </Typography>


          </Box>

          <Box my={2} textAlign='left' paddingLeft={'5%'} paddingRight={'5%'} paddingBottom="5%">

            <Typography style={{ marginTop: '10px' }} variant={'h6'}>
              <strong>{data.contactInfoTitle}</strong>
            </Typography>

          </Box>

          <Box mb={2} paddingLeft={'5%'} paddingRight={'5%'} paddingBottom="5%" >
            <Grid container spacing={3} >
              <Grid item xs={12} sm={4}>
                <DocumentCard
                  title={data.tamariskTitle}
                  data={data.tamariskCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DocumentCard
                  title={data.appraisalNationTitle}
                  data={data.appraisalNationCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DocumentCard
                  title={data.proTeckValuationIntelligenceTitle}
                  data={data.proTeckValuationIntelligenceCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <DocumentCard
                  title={data.clearCapitalTitle}
                  data={data.clearCapitalCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DocumentCard
                  title={data.boweryValidationTitle}
                  data={data.boweryValidationCard}
                  paperBorder={PaperBorderType.NO_BORDER}
                />
              </Grid>
            </Grid>

          </Box>
          <Box m={1} position={'absolute'} right={0} bottom={0} textAlign='right' color={'#ccc'}>
            <Typography variant={'body2'}>
              {`Last Updated: ${format(new Date(data.lastUpdatedDate), 'MM-dd-yyyy')}`}
            </Typography>
          </Box>
        </Box>
      </Page>


    </div>
  );
}